import { Box, Stack, styled, useMediaQuery } from '@mui/material';
import {
  AppConfig,
  AppNavBarMenuOptions,
  AppNavBarMenuOptionsConst,
  CaseDetails,
  CaseNote,
  EconsultUserRoles,
  NoteActionTypes,
  NoteStatusCodes
} from '@oh-vcp/components-common';
import {
  hasCaseChanged,
  hasNoteChanged
} from '@oh-vcp/components-common/src/utils/econsultCaseUtils';
import {
  AppNavDrawer,
  DisplayDesktop,
  DisplayMobile,
  UrlService,
  useNotifierContext
} from '@oh-vcp/components-ui';
import {
  EconsultCaseResultProvider,
  EconsultTabContextProvider,
  useAuth
} from '@oh-vcp/components-web';
import { useAppNavBarContext } from '@oh-vcp/components-web/src/appNavBarContext/AppNavBarContext';
import { useEconsultCaseDetailsContext } from '@oh-vcp/components-web/src/econsultCaseDetailsContext/EconsultCaseDetailsContext';
import CaseService from '@oh-vcp/components-web/src/services/caseService';
import useFileService from '@oh-vcp/components-web/src/services/useFileService';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import EconsultCaseActionPanel from '../econsultCaseActionPanel/EconsultCaseActionPanel';
import EconsultCaseDetailsView from '../econsultCaseDetailsView/EconsultCaseDetailsView';
import EconsultCaseInbox from '../econsultCaseInbox/EconsultCaseInbox';
import EconsultReportsView from '../econsultReportsView/EconsultReportsView';
import { useTheme } from '@mui/system';

const EconsultDetailsStack = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  height: 'calc(100vh - 5.4rem)',
  [theme.breakpoints.up('sm')]: {
    width: 600
  },
  [theme.breakpoints.only('xs')]: {
    height: 'calc(100vh - 4.8rem)'
  }
}));

const EconsultCaseView = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { providerType, caseId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const intervalIdRef = useRef<NodeJS.Timer>();
  const autoSaveTimer = 30000;
  const updatedCaseDetails = useRef<CaseDetails>();
  const previousCaseDetails = useRef<CaseDetails>();
  const updatedDraftNote = useRef<CaseNote>();
  const previousDraftNote = useRef<CaseNote>();
  const draftCaseChanged = useRef<boolean | false>();
  const queryClient = useQueryClient();
  const [appNavBarMenuOptions, setAppNavBarMenuOptions] = useState<AppNavBarMenuOptions>(
    AppNavBarMenuOptionsConst.DefaultOptions
  );
  const { isDrawerOpen } = useAppNavBarContext();
  const {
    setDraftCaseSaveDisable,
    draftCaseSaveDisable,
    setDraftCaseSaved,
    draftCaseSaved,
    setDraftCaseOrNoteChanged,
    returnNoteSaved,
    setReturnNoteSaved,
    fileUploading,
    userRole
  } = useEconsultCaseDetailsContext();
  const { addNotification, removeAllErrorNotifications } = useNotifierContext();
  const [isCreateCase, setIsCreateCase] = useState<boolean>(false);
  const isNodelegate = providerType === 'nodelegate';
  const specialistId = UrlService.getParamIgnoreCase(searchParams, 'specialistId');
  const programId = searchParams?.get('programId');
  const fhirPatientGuid = sessionStorage.getItem('fhirPatientGuid') || undefined;

  if (
    (user?.agreementsToSign || []).length > 0 ||
    (user?.agreementsToReSign || []).length > 0 ||
    (user?.agreements || []).length === 0
  ) {
    const redirectUrl = window.location.href;
    window.location.href = `${AppConfig.baseSelfServeUrl}/#/userAgreement?returnUrl=${redirectUrl}`;
  } else if (user?.passwordExpired) {
    const redirectUrl = window.location.href;
    window.location.href = `${AppConfig.baseSelfServeUrl}/#/expiredPassword?returnUrl=${redirectUrl}`;
  }

  useEffect(() => {
    if (user && user.services) {
      const { directory, econsult, tsm, telederm, hubmessaging, pcvc3 } = user.services;
      const menuOptions = AppNavBarMenuOptionsConst.DefaultOptions;
      if (directory) {
        menuOptions.isDirectoryEnabled = true;
      }
      if (econsult) {
        menuOptions.isEconsultEnabled = true;
      }
      if (tsm) {
        menuOptions.isScheduleEnabled = true;
      }
      if (telederm) {
        menuOptions.isTeledermEnabled = true;
      }
      if (hubmessaging) {
        menuOptions.isMessagingEnabled = true;
      }
      if (pcvc3) {
        menuOptions.isEvisitEnabled = true;
      }
      setAppNavBarMenuOptions(menuOptions);
    }
  }, [appNavBarMenuOptions, user]);

  useEffect(() => {
    if (location.hash.startsWith('#/cases')) {
      console.log(location.hash);
      const urlParts = location.hash.split('/');
      if (urlParts.length > 5) {
        const caseCreate = urlParts[5];
        if (caseCreate.startsWith('create')) {
          setIsCreateCase(true);
          const caseCreateParts = caseCreate.split('?');
          if (caseCreateParts.length > 1) {
            navigate(`/case/${urlParts[3].toLowerCase()}/draft?${caseCreateParts[1]}`);
          } else {
            navigate(`/case/${urlParts[3].toLowerCase()}/draft?create`);
          }
        } else {
          setIsCreateCase(false);
        }
      } else {
        setIsCreateCase(false);
      }
      const urlCaseId = urlParts[urlParts.length - 1];
      if (!isNaN(Number(urlCaseId)) && urlCaseId.trim() !== '') {
        navigate(`/case/search/${urlCaseId}/${urlCaseId}`);
      }
    } else {
      setIsCreateCase(false);
    }
  }, [location, navigate]);

  const getCaseDraftNoteId = async (caseId: number | undefined) => {
    if (!caseId) {
      return undefined;
    }
    const caseInfo = await CaseService.getCaseDetails(caseId);
    const noteType = updatedDraftNote.current?.noteTypeCd;
    const actionType = updatedDraftNote.current?.actionTypeCd;
    const noteContent = updatedDraftNote.current?.noteContent;
    let draftNoteId = undefined;
    if (caseInfo && caseInfo.data && caseInfo.data.notes) {
      caseInfo.data.notes.forEach((note) => {
        if (
          note.statusCd &&
          note.statusCd === NoteStatusCodes.draft &&
          noteType === note.noteTypeCd &&
          (actionType !== NoteActionTypes.finish || NoteActionTypes.finish === note.actionTypeCd)
        ) {
          draftNoteId = note.noteId;
        }
      });
      if (!draftNoteId) {
        caseInfo.data.notes.forEach((note) => {
          if (
            note.statusCd &&
            note.statusCd === NoteStatusCodes.active &&
            noteType === note.noteTypeCd &&
            noteContent === note.noteContent
          ) {
            draftNoteId = -1;
          }
        });
      }
    }
    return draftNoteId;
  };
  const handleDraftNoteSaved = async () => {
    if (!updatedDraftNote || !updatedDraftNote.current) {
      return;
    }
    setDraftCaseSaveDisable(true);
    if (!updatedDraftNote.current.noteId) {
      const draft = await getCaseDraftNoteId(updatedCaseDetails.current?.caseId);
      updatedDraftNote.current.noteId = draft;
    }
    if (!updatedDraftNote.current.noteId || updatedDraftNote.current.noteId !== -1) {
      const note = await CaseService.upsertCaseNote(updatedDraftNote.current);
      const savedNote = JSON.parse(JSON.stringify(note));
      previousDraftNote.current = savedNote;
      updatedDraftNote.current = savedNote;
    } else {
      updatedDraftNote.current = undefined;
    }
    setDraftCaseSaveDisable(false);
    setDraftCaseSaved(true);
  };

  const getCaseNoteStatus = async (caseId: number | undefined, noteId: number | undefined) => {
    if (!caseId || !noteId) {
      return NoteStatusCodes.draft;
    }
    const caseInfo = await CaseService.getCaseDetails(caseId);
    let noteStatus = NoteStatusCodes.draft;
    if (caseInfo && caseInfo.data && caseInfo.data.notes) {
      caseInfo.data.notes.forEach((note) => {
        if (noteId === note.noteId && note.statusCd) {
          noteStatus = note.statusCd;
        }
      });
    }
    return noteStatus;
  };

  const handleDraftSaveTimer = async () => {
    if (
      updatedCaseDetails.current &&
      updatedCaseDetails.current.caseId &&
      updatedCaseDetails.current.statusCd === 'DRAFT' &&
      ((draftCaseChanged.current && draftCaseChanged.current === true) ||
        hasCaseChanged(updatedCaseDetails.current, previousCaseDetails.current))
    ) {
      const draftCaseInfo = await CaseService.getCaseDetails(updatedCaseDetails.current.caseId);
      if (draftCaseInfo && draftCaseInfo.data && draftCaseInfo.data.statusCd === 'DRAFT') {
        setDraftCaseChanged(false);
        setDraftCaseSaveDisable(true);
        if (updatedCaseDetails.current?.participant) {
          if (!updatedCaseDetails.current.participant.participantId) {
            updatedCaseDetails.current.participant.participantId = null;
          }
          updatedCaseDetails.current.participant.statusCd = NoteStatusCodes.draft;
        }
        try {
          await CaseService.upsertCaseDetails(updatedCaseDetails.current);
        } catch (error: any) {
          //OTIX-4585
          if (error?.response?.data?.error === 'E_CASE_VALIDATION_ERROR_REFERRER_SPECIALIST') {
            console.log('E_CASE_VALIDATION_ERROR_REFERRER_SPECIALIST');
          } else {
            removeAllErrorNotifications();
            addNotification({
              type: 'error',
              message: error?.response?.data?.details
            });
          }
          setDraftCaseSaveDisable(false);
          return;
        }
        if (updatedCaseDetails.current) {
          previousCaseDetails.current = JSON.parse(JSON.stringify(updatedCaseDetails.current));
        }
        setTimeout(() => {
          queryClient.invalidateQueries(['caseResults']);
        }, 500);
        setDraftCaseSaveDisable(false);
        setDraftCaseSaved(true);
      } else {
        updatedCaseDetails.current = undefined;
      }
    }
    if (
      updatedDraftNote.current &&
      updatedDraftNote.current.statusCd === 'DRAFT' &&
      hasNoteChanged(previousDraftNote.current, updatedDraftNote.current)
    ) {
      const status = await getCaseNoteStatus(
        updatedCaseDetails.current?.caseId,
        updatedDraftNote.current.noteId
      );
      if (status === NoteStatusCodes.draft) {
        if (
          updatedDraftNote.current.actionTypeCd === NoteActionTypes.unassign ||
          updatedDraftNote.current.actionTypeCd === NoteActionTypes.decline
        ) {
          setReturnNoteSaved(true);
          if (!updatedDraftNote.current.noteId) {
            return;
          }
        }
        handleDraftNoteSaved();
      }
    }
  };

  const isDraftChanged = () => {
    if (draftCaseSaved && !fileUploading && !draftCaseSaveDisable) {
      return false;
    }

    const noteChanged =
      Boolean(updatedDraftNote.current) &&
      updatedDraftNote.current?.statusCd === 'DRAFT' &&
      hasNoteChanged(previousDraftNote.current, updatedDraftNote.current);

    const caseChanged =
      Boolean(updatedCaseDetails.current) &&
      Boolean(updatedCaseDetails.current?.caseId) &&
      updatedCaseDetails.current?.statusCd === 'DRAFT' &&
      (Boolean(draftCaseChanged.current) ||
        (Boolean(previousCaseDetails.current) &&
          hasCaseChanged(updatedCaseDetails.current, previousCaseDetails.current)));

    if (
      updatedDraftNote.current &&
      (updatedDraftNote.current.actionTypeCd === NoteActionTypes.unassign ||
        updatedDraftNote.current.actionTypeCd === NoteActionTypes.decline) &&
      returnNoteSaved
    ) {
      return false;
    }
    if (noteChanged || caseChanged || fileUploading) {
      return true;
    }
    if (updatedCaseDetails && updatedCaseDetails.current) {
      const draftCancelNote = updatedCaseDetails.current.notes?.find(
        (n) => n.statusCd === NoteStatusCodes.draft && n.actionTypeCd === NoteActionTypes.cancel
      );
      if (
        draftCancelNote &&
        draftCancelNote.noteContent &&
        ((draftCancelNote.noteTypeCd === EconsultUserRoles.referrer &&
          (userRole === EconsultUserRoles.referrer ||
            userRole === EconsultUserRoles.referrerDelegate)) ||
          (draftCancelNote.noteTypeCd === EconsultUserRoles.consultant &&
            (userRole === EconsultUserRoles.consultant ||
              userRole === EconsultUserRoles.consultantDelegate)))
      ) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    setDraftCaseOrNoteChanged(isDraftChanged());
  }, [
    updatedCaseDetails.current,
    previousCaseDetails.current,
    updatedDraftNote.current,
    previousDraftNote.current,
    draftCaseChanged.current
  ]);

  useEffect(() => {
    if (!intervalIdRef.current) {
      intervalIdRef.current = setInterval(handleDraftSaveTimer, autoSaveTimer);
      console.log('timer created in caseView, id=' + intervalIdRef.current);
    }
    return () => {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = undefined;
    };
  }, []);
  const setDraftCaseChanged = (caseChanged: boolean) => {
    draftCaseChanged.current = caseChanged;
  };
  const setUpdatedCaseDetails = (updDraftCase: CaseDetails | undefined) => {
    updatedCaseDetails.current = updDraftCase;
  };
  const setUpdatedDraftNote = (updDraftNote: CaseNote | undefined) => {
    updatedDraftNote.current = updDraftNote;
  };
  const setPreviousCaseDetails = (prevDetails: CaseDetails | undefined) => {
    previousCaseDetails.current = prevDetails;
  };
  const setPreviousDraftNote = (prevDraftNote: CaseNote | undefined) => {
    previousDraftNote.current = prevDraftNote;
  };
  const getUpdatedCaseDetails = () => {
    return updatedCaseDetails.current;
  };
  const getPreviousCaseDetails = () => {
    return previousCaseDetails.current;
  };
  const getUpdatedDraftNote = () => {
    return updatedDraftNote.current;
  };
  const getPreviousDraftNote = () => {
    return previousDraftNote.current;
  };

  return (
    <EconsultCaseResultProvider>
      {!isMobile && (
        <>
          <Stack direction="row" flexGrow={1}>
            {!fhirPatientGuid && (
              <AppNavDrawer options={appNavBarMenuOptions} defaultSelected="econsult" />
            )}
            {isNodelegate ? (
              <Box />
            ) : (
              <>
                <EconsultCaseActionPanel
                  isDraftChanged={isDraftChanged}
                  setUpdatedDraftNote={setUpdatedDraftNote}
                  setUpdatedCaseDetails={setUpdatedCaseDetails}
                  getUpdatedDraftNote={getUpdatedDraftNote}
                />
                <EconsultTabContextProvider>
                  {providerType?.toLowerCase() === 'reports' ? (
                    <EconsultDetailsStack>
                      <EconsultReportsView />
                    </EconsultDetailsStack>
                  ) : (
                    <>
                      <EconsultCaseInbox
                        isDraftChanged={isDraftChanged}
                        setUpdatedDraftNote={setUpdatedDraftNote}
                        setUpdatedCaseDetails={setUpdatedCaseDetails}
                        getUpdatedDraftNote={getUpdatedDraftNote}
                      />
                      <EconsultDetailsStack>
                        <EconsultCaseDetailsView
                          setUpdatedCaseDetails={setUpdatedCaseDetails}
                          setPreviousCaseDetails={setPreviousCaseDetails}
                          setUpdatedDraftNote={setUpdatedDraftNote}
                          setPreviousDraftNote={setPreviousDraftNote}
                          setDraftCaseChanged={setDraftCaseChanged}
                          getUpdatedCaseDetails={getUpdatedCaseDetails}
                          getPreviousCaseDetails={getPreviousCaseDetails}
                          getUpdatedDraftNote={getUpdatedDraftNote}
                          getPreviousDraftNote={getPreviousDraftNote}
                          isDraftChanged={isDraftChanged}
                        />
                      </EconsultDetailsStack>
                    </>
                  )}
                </EconsultTabContextProvider>
              </>
            )}
          </Stack>
        </>
      )}
      {isMobile && (
        <>
          <EconsultTabContextProvider>
            {!fhirPatientGuid && (
              <AppNavDrawer
                open={isDrawerOpen}
                variant="temporary"
                options={appNavBarMenuOptions}
                defaultSelected="econsult"
              />
            )}
            {isNodelegate ? (
              <Box />
            ) : (
              <>
                {((!providerType && !caseId) || specialistId || programId || isCreateCase) && (
                  <EconsultCaseActionPanel
                    isDraftChanged={isDraftChanged}
                    setUpdatedDraftNote={setUpdatedDraftNote}
                    setUpdatedCaseDetails={setUpdatedCaseDetails}
                    getUpdatedDraftNote={getUpdatedDraftNote}
                  />
                )}
                {providerType && providerType !== 'reports' && !caseId && (
                  <EconsultCaseInbox
                    isDraftChanged={isDraftChanged}
                    setUpdatedDraftNote={setUpdatedDraftNote}
                    setUpdatedCaseDetails={setUpdatedCaseDetails}
                    getUpdatedDraftNote={getUpdatedDraftNote}
                  />
                )}
                {providerType && providerType === 'reports' && !caseId && <EconsultReportsView />}
                {providerType && caseId && (
                  <EconsultDetailsStack>
                    <EconsultCaseDetailsView
                      setUpdatedCaseDetails={setUpdatedCaseDetails}
                      setPreviousCaseDetails={setPreviousCaseDetails}
                      setUpdatedDraftNote={setUpdatedDraftNote}
                      setPreviousDraftNote={setPreviousDraftNote}
                      setDraftCaseChanged={setDraftCaseChanged}
                      getUpdatedCaseDetails={getUpdatedCaseDetails}
                      getPreviousCaseDetails={getPreviousCaseDetails}
                      getUpdatedDraftNote={getUpdatedDraftNote}
                      getPreviousDraftNote={getPreviousDraftNote}
                      isDraftChanged={isDraftChanged}
                    />
                  </EconsultDetailsStack>
                )}
              </>
            )}
          </EconsultTabContextProvider>
        </>
      )}
    </EconsultCaseResultProvider>
  );
};

export default EconsultCaseView;
